import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Avatar,
  Chip,
  LinearProgress,
  Badge,
  Alert,
  Skeleton,
  Fade
} from '@mui/material';
import {
  Build,
  Engineering,
  Security,
  Assignment,
  HomeWork,
  TrendingUp,
  People,
  ShoppingCart,
  Star,
  Verified
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../utils/helpers';
import { brand } from '../theme/colors';

const MarketplacePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [marketplaceStats, setMarketplaceStats] = useState({});

  // Enhanced marketplace services with statistics and features
  const marketplaceServices = [
    {
      id: 'materials',
      title: 'Construction Materials',
      description: 'Premium building materials from verified suppliers. Bulk pricing, quality guaranteed, fast delivery.',
      icon: <HomeWork sx={{ fontSize: 40 }} />,
      path: '/marketplace/materials',
      color: brand.primary,
      gradient: brand.gradient,
      stats: {
        vendors: '45+ Suppliers',
        products: '1,200+ Products',
        rating: 4.8,
        savings: 'Up to 25% bulk savings'
      },
      features: ['Bulk Pricing', 'Same-Day Delivery', 'SABS Certified', 'Project Integration'],
      badge: 'Most Popular',
      badgeColor: 'warning'
    },
    {
      id: 'equipment',
      title: 'Equipment Rental',
      description: 'Professional construction equipment with certified operators. Daily, weekly, monthly rates.',
      icon: <Build sx={{ fontSize: 40 }} />,
      path: '/marketplace/equipment',
      color: brand.secondary,
      gradient: `linear-gradient(135deg, ${brand.secondary} 0%, ${brand.primary} 100%)`,
      stats: {
        vendors: '25+ Rental Companies',
        products: '350+ Equipment',
        rating: 4.9,
        availability: '95% uptime guarantee'
      },
      features: ['Certified Operators', 'GPS Tracking', '24/7 Support', 'Insurance Covered'],
      badge: 'Premium',
      badgeColor: 'primary'
    },
    {
      id: 'professionals',
      title: 'Professional Services',
      description: 'Certified architects, engineers, and consultants. Portfolios, ratings, instant booking.',
      icon: <Engineering sx={{ fontSize: 40 }} />,
      path: '/marketplace/professionals',
      color: '#f093fb',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      stats: {
        vendors: '180+ Professionals',
        services: '12 Specializations',
        rating: 4.7,
        experience: 'Avg 8+ years'
      },
      features: ['Verified Credentials', 'Portfolio Reviews', 'Instant Booking', 'Project Management'],
      badge: 'Verified',
      badgeColor: 'success'
    },
    {
      id: 'insurance',
      title: 'Construction Insurance',
      description: 'Comprehensive construction insurance policies. Compare quotes, instant coverage.',
      icon: <Security sx={{ fontSize: 40 }} />,
      path: '/marketplace/insurance',
      color: '#4facfe',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      stats: {
        vendors: '8 Insurance Providers',
        policies: '15+ Policy Types',
        rating: 4.6,
        coverage: 'Up to $2M coverage'
      },
      features: ['Instant Quotes', 'Digital Policies', 'Claims Support', 'Competitive Rates'],
      badge: 'Protected',
      badgeColor: 'info'
    },
    {
      id: 'inspectors',
      title: 'Quality Inspectors',
      description: 'Certified inspection services. Schedule inspections, get detailed reports instantly.',
      icon: <Assignment sx={{ fontSize: 40 }} />,
      path: '/marketplace/inspectors',
      color: '#43e97b',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      stats: {
        vendors: '35+ Certified Inspectors',
        services: '8 Inspection Types',
        rating: 4.8,
        turnaround: '24-hour reports'
      },
      features: ['ZBCA Certified', 'Digital Reports', 'Same Day Service', 'Photo Documentation'],
      badge: 'Certified',
      badgeColor: 'success'
    }
  ];

  // Load marketplace statistics
  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        setMarketplaceStats({
          totalVendors: 293,
          totalProducts: 2847,
          totalBookings: 1456,
          averageRating: 4.8,
          totalSavings: 580000
        });
        setLoading(false);
      }, 1000);
    };

    loadStats();
  }, []);

  const handleServiceClick = (path) => {
    navigate(path);
  };

  const StatCard = ({ title, value, subtitle, color = 'primary' }) => (
    <Card sx={{ textAlign: 'center', height: '100%' }}>
      <CardContent>
        <Typography variant="h4" color={color} sx={{ fontWeight: 700, mb: 1 }}>
          {loading ? <Skeleton width={80} /> : value}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={800}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Eqaya Marketplace
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', mb: 3 }}>
            Your comprehensive construction ecosystem. Connect with suppliers, rent equipment, 
            hire professionals, secure insurance, and book quality inspections.
          </Typography>
          
          <Alert severity="success" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
            🎉 Over {marketplaceStats.totalVendors || '290+'} verified vendors • 
            {formatCurrency(marketplaceStats.totalSavings || 580000)} saved by our users this year!
          </Alert>
        </Box>
      </Fade>

      {/* Statistics Dashboard */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        <Grid item xs={6} md={3}>
          <StatCard 
            title="Verified Vendors" 
            value={marketplaceStats.totalVendors || '293'}
            subtitle="Across all categories"
            color="primary"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard 
            title="Products & Services" 
            value={marketplaceStats.totalProducts || '2,847'}
            subtitle="Ready to order"
            color="success"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard 
            title="Successful Bookings" 
            value={marketplaceStats.totalBookings || '1,456'}
            subtitle="This month"
            color="info"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard 
            title="Average Rating" 
            value={`${marketplaceStats.averageRating || '4.8'} ⭐`}
            subtitle="From verified reviews"
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Enhanced Marketplace Services */}
      <Grid container spacing={4}>
        {marketplaceServices.map((service, index) => (
          <Fade in timeout={1000 + (index * 200)} key={service.id}>
            <Grid item xs={12} md={6} lg={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: `linear-gradient(135deg, ${service.color}15 0%, ${service.color}08 100%)`,
                  border: '1px solid',
                  borderColor: `${service.color}30`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    '& .service-bg': {
                      opacity: 0.1
                    }
                  }
                }}
              >
                {/* Background Pattern */}
                <Box 
                  className="service-bg"
                  sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: 150,
                    height: 150,
                    background: service.gradient,
                    borderRadius: '50%',
                    opacity: 0.05,
                    transition: 'opacity 0.3s ease'
                  }} 
                />

                <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 3, position: 'relative', zIndex: 1 }}>
                  {/* Badge */}
                  {service.badge && (
                    <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                      <Chip 
                        label={service.badge} 
                        size="small" 
                        color={service.badgeColor || 'default'}
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                  )}

                  {/* Icon */}
                  <Avatar
                    sx={{
                      background: service.gradient,
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      boxShadow: `0 8px 32px ${service.color}40`
                    }}
                  >
                    {service.icon}
                  </Avatar>
                  
                  <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
                    {service.title}
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {service.description}
                  </Typography>

                  {/* Statistics */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="caption" color="text.secondary">
                          {Object.keys(service.stats)[0].replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {service.stats[Object.keys(service.stats)[0]]}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="caption" color="text.secondary">
                          Rating
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {service.stats.rating}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Features */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', mb: 2 }}>
                    {service.features.slice(0, 2).map((feature, idx) => (
                      <Chip 
                        key={idx} 
                        label={feature} 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                    {service.features.length > 2 && (
                      <Chip 
                        label={`+${service.features.length - 2}`} 
                        size="small" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                </CardContent>
                
                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={() => handleServiceClick(service.path)}
                    sx={{
                      background: service.gradient,
                      fontWeight: 600,
                      textTransform: 'none',
                      py: 1.5,
                      boxShadow: `0 4px 20px ${service.color}40`,
                      '&:hover': {
                        background: service.gradient,
                        boxShadow: `0 8px 30px ${service.color}50`,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Explore {service.title.split(' ')[0]}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Fade>
        ))}
      </Grid>

      {/* Enhanced Value Proposition */}
      <Box sx={{ mt: 8, p: 6, background: `linear-gradient(135deg, ${brand.primary}15 0%, ${brand.secondary}15 100%)`, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 4 }}>
          Why Choose Eqaya Marketplace?
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'success.main', width: 60, height: 60, mx: 'auto', mb: 2 }}>
                <Verified sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                100% Verified Providers
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                Every supplier and professional undergoes rigorous verification. Government registration, 
                insurance coverage, and community ratings ensure quality.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip label="Background Checked" size="small" color="success" sx={{ mr: 1 }} />
                <Chip label="Insured" size="small" color="success" />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'info.main', width: 60, height: 60, mx: 'auto', mb: 2 }}>
                <ShoppingCart sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Integrated Smart Payments
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                Secure blockchain-powered payments with mobile money integration. 
                Milestone-based wallet protection for both parties.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip label="Blockchain Secured" size="small" color="info" sx={{ mr: 1 }} />
                <Chip label="Mobile Money" size="small" color="info" />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: 'warning.main', width: 60, height: 60, mx: 'auto', mb: 2 }}>
                <TrendingUp sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                Complete Project Integration
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                Marketplace purchases automatically sync with your construction projects. 
                Track expenses, manage vendors, monitor deliveries.
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Chip label="Auto Sync" size="small" color="warning" sx={{ mr: 1 }} />
                <Chip label="Expense Tracking" size="small" color="warning" />
              </Box>
            </Box>
          </Grid>
        </Grid>
        
        {/* CTA Section */}
        <Box sx={{ textAlign: 'center', mt: 6, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Ready to build something amazing?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
            Join thousands of satisfied customers who have saved time and money using Eqaya Marketplace.
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            sx={{ 
              px: 4, 
              py: 1.5,
              background: `linear-gradient(45deg, ${brand.primary} 30%, ${brand.secondary} 90%)`,
              fontWeight: 600,
              textTransform: 'none'
            }}
            onClick={() => navigate('/marketplace/materials')}
          >
            Start Shopping Now
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default MarketplacePage;